import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Plus,
  AlertTriangle,
  Target,
} from 'lucide-react';
import {
  mockTAO6InstanceDetails,
  TAO6PretrainedModel,
  TAO6Dataset,
  TAO6Experiment,
  TAO6TaskType,
} from '@/mock/gpu-instance';
import { TAO6TrainingMonitor, TAO6ExperimentCreator } from '../../components/tao';
import { TAO6TrainingConfig } from '../../components/tao/TAO6ConfigBuilder';

const TAOExperimentsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [experiments, setExperiments] = useState<TAO6Experiment[]>([]);

  const details = useMemo(() => {
    const data = mockTAO6InstanceDetails(instanceId || '');
    // Initialize experiments from mock data if empty
    if (experiments.length === 0) {
      setExperiments(data.experiments);
    }
    return data;
  }, [instanceId]);

  const handleCreateExperiment = (newExperiment: {
    name: string;
    task: TAO6TaskType;
    model: TAO6PretrainedModel;
    dataset: TAO6Dataset;
    config: TAO6TrainingConfig;
  }) => {
    const experiment: TAO6Experiment = {
      id: `exp-${Date.now()}`,
      name: newExperiment.name,
      task: newExperiment.task,
      pretrainedModel: newExperiment.model,
      dataset: newExperiment.dataset,
      status: 'queued',
      progress: 0,
      config: newExperiment.config,
      metrics: {
        currentEpoch: 0,
        totalEpochs: newExperiment.config.epochs,
        trainLoss: 0,
      },
      training: {
        startedAt: 0,
        gpuUtilization: 0,
        gpuMemoryUsed: 0,
      },
      exports: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    setExperiments((prev) => [experiment, ...prev]);
  };

  const handleViewDetails = (experiment: TAO6Experiment) => {
    // In a real app, this would navigate to a detailed experiment view
    console.log('View experiment details:', experiment);
  };

  const handleExport = (experiment: TAO6Experiment) => {
    // In a real app, this would trigger the export dialog
    console.log('Export experiment:', experiment);
  };

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-green-500" />
            TAO Experiments
          </h1>
          <p className="text-muted-foreground">
            Create and manage training experiments with TAO 6.x
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Experiment
        </Button>
      </div>

      {/* Experiments List */}
      <TAO6TrainingMonitor
        experiments={experiments.length > 0 ? experiments : details.experiments}
        onViewDetails={handleViewDetails}
        onExport={handleExport}
      />

      {/* Create Experiment Dialog */}
      <TAO6ExperimentCreator
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        models={details.pretrainedModels}
        datasets={details.datasets}
        onCreateExperiment={handleCreateExperiment}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> Experiment creation is simulated.
          In production, experiments will be submitted to the TAO REST API and run on actual GPU hardware.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TAOExperimentsPage;
